import axios, { CancelTokenSource } from "axios";
import _ from "lodash";
import React, { Component, createContext } from "react";
import { generateEntries } from "services/entryServices";
import { QuantityValidationErrorMessage, QuantityValidationErrorType } from "ts-types/api.enums";
import {
  BaseEntryDto,
  EntryTable,
  PatientEntryRequest,
  ProcedureCategory,
  SearchProceduresRequest,
  SearchProceduresResult,
  TarmedDataDto
} from "ts-types/api.types";
import { generateUUID } from "utils/generatorUtils";
import { findEntryPredicate } from "utils/predicateUtils";
import { isReferenceProcedure } from "utils/tarmedUtils";
import { generateTarmedErrorMessage, TarmedErrorMessage } from "utils/validatorUtils";

const cancelTokenSource = axios.CancelToken.source();

interface Props extends JSX.ElementChildrenAttribute {
}

export interface ExtendedBaseEntryDto extends TarmedDataDto {
}

export interface AddEntryResponse {
  entry?: ExtendedBaseEntryDto,
  errorMessages?: Array<TarmedErrorMessage>
}

type AddEntryResponseType = Promise<AddEntryResponse | undefined> | TarmedErrorMessage | undefined;

export type MedicamentChanges = { [medId: number]: number };

interface State {
  initialEntries: Array<ExtendedBaseEntryDto>,
  currentEntries: Array<ExtendedBaseEntryDto>,
  emergencyProcedure?: BaseEntryDto,
  bastardProcedure?: BaseEntryDto,
  setCurrentEntries: (entries: Array<ExtendedBaseEntryDto>) => void,
  addEntry: (entry: Partial<SearchProceduresResult>,
             procedureCategory: ProcedureCategory,
             parentId: number | undefined,
             electronicDataTransfer: boolean,
             filterData: Partial<SearchProceduresRequest>,
             entryTable: EntryTable) => AddEntryResponseType,
  editEntry: (entry: ExtendedBaseEntryDto) => ExtendedBaseEntryDto | undefined,
  deleteEntry: (entry: ExtendedBaseEntryDto) => void,
  saveEntry: (entry: ExtendedBaseEntryDto) => void,
  saveTarmedEntry: (entry: ExtendedBaseEntryDto) => void,
  plusQuantity: (entry: ExtendedBaseEntryDto) => void,
  minusQuantity: (entry: ExtendedBaseEntryDto) => boolean,
  calculateCostEstimationQuantities: (costEstimationEntries: Array<Partial<SearchProceduresResult>>) => Array<Partial<SearchProceduresResult>>,
  updateEmergencyData: (updatedTarmedEntries: Array<TarmedDataDto>,
                        emergencyProcedure?: BaseEntryDto,
                        bastardProcedure?: BaseEntryDto) => void,
  getTarmedEntries: () => Array<TarmedDataDto>,
  getMedicamentsChanges: () => MedicamentChanges,
  loadingEmergencyData: boolean,
  setLoadingEmergencyData: (loadingEmergencyData: boolean) => void,
  resetEmergencyData: () => void,
  priceTotal: number,
  unitText?: string,
  cancelTokenSource: CancelTokenSource
}

export const EntriesContext = createContext<State>({
  initialEntries: [],
  currentEntries: [],
  setCurrentEntries: (entries: Array<ExtendedBaseEntryDto>) => {
  },
  addEntry: (entry: Partial<SearchProceduresResult>,
             procedureCategory: ProcedureCategory,
             parentId: number | undefined,
             electronicDataTransfer: boolean,
             filterData: Partial<SearchProceduresRequest>) => undefined,
  editEntry: (entr: ExtendedBaseEntryDto) => undefined,
  saveEntry: (entry: ExtendedBaseEntryDto) => {
  },
  saveTarmedEntry: (entry: ExtendedBaseEntryDto) => {
  },
  deleteEntry: (entry: ExtendedBaseEntryDto) => {
  },
  plusQuantity: (entry: ExtendedBaseEntryDto) => {
  },
  minusQuantity: (entry: ExtendedBaseEntryDto) => false,
  calculateCostEstimationQuantities: (costEstimationEntries: Array<Partial<SearchProceduresResult>>) => [],
  updateEmergencyData: (updatedTarmedEntries: Array<TarmedDataDto>,
                        emergencyProcedure?: BaseEntryDto,
                        bastardProcedure?: BaseEntryDto) => {
  },
  getTarmedEntries: () => [],
  getMedicamentsChanges: () => ({}),
  loadingEmergencyData: false,
  setLoadingEmergencyData: (loadingEmergencyData: boolean) => {
  },
  resetEmergencyData: () => {
  },
  priceTotal: 0,
  unitText: undefined,
  cancelTokenSource
});

export class EntriesProvider extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    this.state = {
      initialEntries: [],
      currentEntries: [],
      setCurrentEntries: this.setCurrentEntries,
      addEntry: this.addEntry,
      editEntry: this.editEntry,
      saveEntry: this.saveEntry,
      saveTarmedEntry: this.saveTarmedEntry,
      deleteEntry: this.deleteEntry,
      plusQuantity: this.plusQuantity,
      minusQuantity: this.minusQuantity,
      calculateCostEstimationQuantities: this.calculateCostEstimationQuantities,
      updateEmergencyData: this.updateEmergencyData,
      getTarmedEntries: this.getTarmedEntries,
      getMedicamentsChanges: this.getMedicamentsChanges,
      loadingEmergencyData: false,
      setLoadingEmergencyData: this.setLoadingEmergencyData,
      resetEmergencyData: this.resetEmergencyData,
      priceTotal: 0,
      unitText: undefined,
      cancelTokenSource
    };
  }

  componentWillUnmount(): void {

    const { cancelTokenSource } = this.state;
    cancelTokenSource.cancel("Operation canceled by user");
  }

  calculatePriceForBulksAndDescendants = (parentEntry: ExtendedBaseEntryDto): number => {

    let totalPrice = 0;

    if (parentEntry.additionalEfforts) {
      totalPrice += parentEntry.additionalEfforts
        .map(entry => entry.quantity * entry.value)
        .reduce((entry1, entry2) => entry1 + entry2, 0);
    }

    if (parentEntry.bulks) {
      totalPrice += parentEntry.bulks
        .map(entry => entry.code != parentEntry.code ? entry.quantity * entry.calculatedValue : 0)
        .reduce((entry1, entry2) => entry1 + entry2, 0);

      // calculate for bastard procedures
      totalPrice += parentEntry.bulks
        .map(entry => entry.code != parentEntry.code && entry.bastardProcedure ?
          entry.bastardProcedure.quantity * entry.bastardProcedure.calculatedValue : 0)
        .reduce((entry1, entry2) => entry1 + entry2, 0);
    }

    if (parentEntry.bastardProcedure) {
      totalPrice += parentEntry.bastardProcedure.quantity * parentEntry.bastardProcedure.calculatedValue;
    }

    if (parentEntry.descendants) {
      totalPrice += parentEntry.descendants
        .map(entry => entry.quantity * entry.calculatedValue)
        .reduce((entry1, entry2) => entry1 + entry2, 0);
    }

    return totalPrice;
  };

  reCalculatePrices = (entries?: Array<ExtendedBaseEntryDto>) => {

    const { currentEntries, priceTotal } = this.state;

    let entriesToSum = currentEntries;

    if (entries) {
      entriesToSum = entries;
    }

    const newPriceTotal = entriesToSum
      .map(entry => entry.quantity * entry.calculatedValue + this.calculatePriceForBulksAndDescendants(entry))
      .reduce((entry1, entry2) => entry1 + entry2, 0);

    if (priceTotal != newPriceTotal) {
      this.setState({
        priceTotal: newPriceTotal
      });
    }
  };

  setCurrentEntries = (entries: Array<ExtendedBaseEntryDto>) => {

    this.reCalculatePrices(entries);

    this.setState({
      initialEntries: [...entries],
      currentEntries: entries
    });
  };

  setLoadingEmergencyData = (loadingEmergencyData: boolean) => {
    this.setState({
      loadingEmergencyData: loadingEmergencyData
    });
  };

  resetEmergencyData = () => {
    this.setState({
      bastardProcedure: undefined,
      emergencyProcedure: undefined
    });
  };

  getTarmedEntries = (): Array<TarmedDataDto> => {

    const { currentEntries, emergencyProcedure } = this.state;

    return currentEntries
      .filter(entry => entry.tarmedProcedure
        && (!emergencyProcedure || emergencyProcedure.code != entry.code));
  };

  getTarmedProcedureCodes = (): Array<string> => {

    const { currentEntries } = this.state;
    const tarmedProcedureCodes: Array<string> = [];

    currentEntries
      .filter(entry => entry.tarmedProcedure)
      .map(entry => {
        if (entry.bulks) {
          entry.bulks.forEach(bulk => tarmedProcedureCodes.push(bulk.code));
        } else {
          tarmedProcedureCodes.push(entry.code);
        }

        if (entry.descendants) {
          entry.descendants.forEach(descendant => tarmedProcedureCodes.push(descendant.code));
        }
      });

    return tarmedProcedureCodes;
  };

  findEntryIndexInSearchResult = (
    aEntry: Partial<SearchProceduresResult>,
    procedureCategory: ProcedureCategory,
    aEntries: ExtendedBaseEntryDto[]): number => {

    return aEntries.findIndex(
      currentEntry =>
        (
          (procedureCategory !== "MEDICAMENT" && aEntry.code == currentEntry.code)
          || (procedureCategory === "MEDICAMENT" && aEntry.id === currentEntry.medicamentId)
        ));
  };

  findEntryIndex = (
    aEntry: Partial<ExtendedBaseEntryDto>,
    aEntries: ExtendedBaseEntryDto[]): number => {

    return aEntries.findIndex(
      currentEntry =>
        (
          (_.isNil(aEntry.medicamentId) && aEntry.code == currentEntry.code)
          || (!_.isNil(aEntry.medicamentId) && aEntry.medicamentId === currentEntry.medicamentId)
        ));
  };

  addEntry = (entry: Partial<SearchProceduresResult>,
              procedureCategory: ProcedureCategory,
              parentId: number | undefined,
              electronicDataTransfer: boolean,
              filterData: Partial<SearchProceduresRequest>,
              entryTable: EntryTable): AddEntryResponseType => {

    const { currentEntries } = this.state;

    const foundEntryIndex = this.findEntryIndexInSearchResult(entry, procedureCategory, currentEntries);

    if (foundEntryIndex != -1) {

      const foundEntry = currentEntries[foundEntryIndex];

      if (foundEntry.tarmedProcedure) {
        return {
          messageKey: "procedure.alreadyExists",
          procedureCode: foundEntry.code
        };
      }

      const modifiedEntries = Array.from(currentEntries);

      modifiedEntries[foundEntryIndex] = {
        ...currentEntries[foundEntryIndex],
        quantity: currentEntries[foundEntryIndex].quantity + 1
      };

      if (modifiedEntries[foundEntryIndex].additionalEfforts) {
        modifiedEntries[foundEntryIndex].additionalEfforts.forEach(additionalEffort => {
          additionalEffort.quantity = additionalEffort.quantity + 1;
        });
      }

      this.reCalculatePrices(modifiedEntries);

      this.setState({
        currentEntries: modifiedEntries
      });

    } else {

      const tarmedProcedureCodes: Array<string> = this.getTarmedProcedureCodes();
      const { bastardProcedure, emergencyProcedure, cancelTokenSource } = this.state;

      const entryRequest: PatientEntryRequest = {
        id: entry.id!,
        tariffId: filterData.tariffId!,
        patientId: filterData.patientId,
        treatmentId: filterData.treatmentId,
        mandateId: filterData.mandateId!,
        date: filterData.validDate!,
        time: filterData.validTime,
        procedureCategory: procedureCategory,
        emergencyData: filterData.emergencyData,
        electronicDataTransfer: electronicDataTransfer,
        tarmedProcedureCodes: tarmedProcedureCodes,
        bastardProcedure: bastardProcedure,
        entryTable: entryTable
      };

      return generateEntries(entryRequest, cancelTokenSource)
        .then(response => {

          const modifiedEntries = Array.from(currentEntries);

          this.setState({
            unitText: response.unitText
          });

          if (procedureCategory == "BLOCK_PROCEDURE") {

            const allErrorMessages: Array<TarmedErrorMessage> = [];
            const errorMessages = response.errorMessages;

            if (!_.isEmpty(errorMessages)) {

              const mappedErrorMessages = errorMessages.map(errorMessage => ({
                messageKey: errorMessage
              }));

              allErrorMessages.push(...mappedErrorMessages);
            }

            const responseEntries = response.entries;
            responseEntries && responseEntries.forEach((entry: BaseEntryDto) => {

              let procedure: ExtendedBaseEntryDto;

              const foundEntryIndex = modifiedEntries.findIndex(
                currentEntry =>
                  (!_.isNil(currentEntry.procedureId) && currentEntry.code == entry.code)
                  || (!_.isNil(currentEntry.medicamentId) && currentEntry.medicamentId == entry.medicamentId)
              );

              if (foundEntryIndex !== -1) {
                if (entry.tarmedProcedure) {
                  allErrorMessages.push({
                    messageKey: "procedure.alreadyExists",
                    procedureCode: entry.code
                  });
                } else {
                  modifiedEntries[foundEntryIndex] = {
                    ...modifiedEntries[foundEntryIndex],
                    quantity: modifiedEntries[foundEntryIndex].quantity + entry.quantity
                  };

                  const { additionalEfforts, quantity } = modifiedEntries[foundEntryIndex];

                  if (additionalEfforts) {
                    additionalEfforts.forEach(additionalEffort => {
                      additionalEffort.quantity = quantity;
                    });
                  }
                }
              } else {
                if (entry.tarmedProcedure) {
                  if (foundEntryIndex !== -1) {
                    allErrorMessages.push({
                      messageKey: "procedure.alreadyExists",
                      procedureCode: entry.code
                    });
                  } else {
                    procedure = {
                      ...entry,
                      id: -1,
                      bulks: response.bulks[entry.code],
                      masters: response.masters,
                      descendants: response.descendants[entry.code],
                      sides: response.sides[entry.code],
                      tarmedProcedure: entry.tarmedProcedure
                    };

                    modifiedEntries.push(procedure);
                  }
                } else {
                  procedure = {
                    ...entry,
                    id: -1
                  };

                  modifiedEntries.push(procedure);
                }
              }
            });

            if (currentEntries.length == 0 && emergencyProcedure) {
              modifiedEntries.push(emergencyProcedure);
            }

            this.reCalculatePrices(modifiedEntries);

            this.setState({
              currentEntries: modifiedEntries
            });

            if (!_.isEmpty(allErrorMessages)) {
              return {
                errorMessages: allErrorMessages
              };
            }
          } else if (!response.isTarmed) {
            response.entries.forEach(entry => {

              const foundEntryIndex = this.findEntryIndex(entry, currentEntries);

              if (foundEntryIndex != -1) {

                modifiedEntries[foundEntryIndex] = {
                  ...modifiedEntries[foundEntryIndex],
                  quantity: modifiedEntries[foundEntryIndex].quantity + 1
                };

              } else {
                modifiedEntries.push(entry);
              }
            });

            this.reCalculatePrices(modifiedEntries);

            this.setState({
              currentEntries: modifiedEntries
            });

            if (entry.askAmount || response.hasDosages) {
              return {
                entry: response.entries[0]
              };
            }
          } else {

            const validation = response.quantityValidation;
            const errorMessages = response.errorMessages;

            if (validation && validation.status == "INVALID") {

              const errorMessage: QuantityValidationErrorMessage = (validation.errorMessage as QuantityValidationErrorMessage);
              const errorType: QuantityValidationErrorType = (validation.errorType as QuantityValidationErrorType);

              return {
                errorMessages: [
                  generateTarmedErrorMessage(
                    errorMessage,
                    errorType,
                    validation.remainingQuantity,
                    validation.maxQuantity,
                    validation.constraintQuantity
                  )
                ]
              };
            }

            if (!_.isEmpty(errorMessages)) {

              const mappedErrorMessages = errorMessages.map(errorMessage => ({
                messageKey: errorMessage
              }));

              return {
                errorMessages: mappedErrorMessages
              };
            }

            // in a consultation a reference procedure must never be added on its own: it has to be
            // entered through one of its master procedures, so hand it back to the caller instead
            const isConsultationReferenceProcedure = entryTable == "CONSULTATION"
              && isReferenceProcedure(response.entries[0])
              && !_.isEmpty(response.masters);

            if (!response.shouldOpenModal && !isConsultationReferenceProcedure) {
              response.entries.forEach((entry: BaseEntryDto) => {

                const tarmedProcedure: ExtendedBaseEntryDto = {
                  ...entry,
                  bulks: response.bulks[entry.code],
                  masters: response.masters,
                  descendants: response.descendants[entry.code],
                  sides: response.sides[entry.code],
                  tarmedProcedure: entry.tarmedProcedure
                };

                modifiedEntries.push(tarmedProcedure);
              });

              if (currentEntries.length == 0 && emergencyProcedure) {
                modifiedEntries.push(emergencyProcedure);
              }

              this.reCalculatePrices(modifiedEntries);

              this.setState({
                currentEntries: modifiedEntries
              });
            } else {
              return {
                entry: {
                  ...response.entries[0],
                  bulks: response.bulks[response.entries[0].code],
                  masters: response.masters,
                  descendants: response.descendants[response.entries[0].code],
                  sides: response.sides[response.entries[0].code],
                  tarmedProcedure: true
                }
              };
            }
          }
        });
    }
  };

  editEntry = (entry: ExtendedBaseEntryDto): ExtendedBaseEntryDto | undefined => {

    const { currentEntries } = this.state;

    const entryIx = this.findEntryIndex(entry, currentEntries);

    if (entryIx != -1) {
      return currentEntries[entryIx];
    }
  };

  deleteEntry = (entry: ExtendedBaseEntryDto) => {

    const { currentEntries } = this.state;

    const entryIndex = this.findEntryIndex(entry, currentEntries);

    if (entryIndex != -1) {

      const modifiedEntries = Array.from(currentEntries);

      modifiedEntries.splice(entryIndex, 1);

      this.reCalculatePrices(modifiedEntries);

      this.setState({
        currentEntries: modifiedEntries
      });
    }
  };

  saveEntry = (entry: ExtendedBaseEntryDto) => {

    const { currentEntries } = this.state;

    const foundEntryIndex = this.findEntryIndex(entry, currentEntries);

    if (foundEntryIndex != -1) {

      const modifiedEntries = Array.from(currentEntries);

      modifiedEntries[foundEntryIndex] = {
        ...modifiedEntries[foundEntryIndex],
        ...entry
      };

      this.reCalculatePrices(modifiedEntries);

      this.setState({
        currentEntries: modifiedEntries
      });
    } else if (!entry.procedureId && !entry.medicamentId) {

      const modifiedEntries = Array.from(currentEntries);

      // Generate a unique code
      entry.code = generateUUID();

      modifiedEntries.push(entry);

      this.reCalculatePrices(modifiedEntries);

      this.setState({
        currentEntries: modifiedEntries
      });
    }
  };

  saveTarmedEntry = (entry: ExtendedBaseEntryDto) => {

    const { currentEntries, emergencyProcedure } = this.state;

    const foundEntryIndex = this.findEntryIndex(entry, currentEntries);

    const modifiedEntries = Array.from(currentEntries);

    if (foundEntryIndex == -1) {

      modifiedEntries.push(entry);

      if (currentEntries.length == 0 && emergencyProcedure) {
        modifiedEntries.push(emergencyProcedure);
      }

      this.reCalculatePrices(modifiedEntries);

    } else {
      modifiedEntries[foundEntryIndex] = { ...entry };
    }

    this.setState({
      currentEntries: modifiedEntries
    });
  };

  plusQuantity = (entry: ExtendedBaseEntryDto) => {

    const { currentEntries } = this.state;
    const foundEntryIndex = this.findEntryIndex(entry, currentEntries);

    if (foundEntryIndex != -1) {

      const modifiedEntries = Array.from(currentEntries);

      modifiedEntries[foundEntryIndex] = {
        ...modifiedEntries[foundEntryIndex],
        quantity: modifiedEntries[foundEntryIndex].quantity + 1
      };

      if (modifiedEntries[foundEntryIndex].additionalEfforts) {
        modifiedEntries[foundEntryIndex].additionalEfforts.forEach(additionalEffort => {
          additionalEffort.quantity = modifiedEntries[foundEntryIndex].quantity;
        });
      }

      this.reCalculatePrices(modifiedEntries);

      this.setState({
        currentEntries: modifiedEntries
      });
    }
  };

  minusQuantity = (entry: ExtendedBaseEntryDto): boolean => {

    const { currentEntries } = this.state;
    const foundEntryIndex = this.findEntryIndex(entry, currentEntries);

    let removed = false;

    if (foundEntryIndex != -1) {

      const modifiedEntries = Array.from(currentEntries);

      modifiedEntries[foundEntryIndex] = {
        ...modifiedEntries[foundEntryIndex],
        quantity: modifiedEntries[foundEntryIndex].quantity - 1
      };

      if (modifiedEntries[foundEntryIndex].additionalEfforts) {
        modifiedEntries[foundEntryIndex].additionalEfforts.forEach(additionalEffort => {
          additionalEffort.quantity = modifiedEntries[foundEntryIndex].quantity;
        });
      }

      if (modifiedEntries[foundEntryIndex].quantity == 0) {
        modifiedEntries.splice(foundEntryIndex, 1);
        removed = true;
      }

      this.reCalculatePrices(modifiedEntries);

      this.setState({
        currentEntries: modifiedEntries
      });
    }

    return removed;
  };

  calculateCostEstimationQuantities = (
    costEstimationEntries: Array<Partial<SearchProceduresResult>>
  ): Array<Partial<SearchProceduresResult>> => {

    const { currentEntries } = this.state;
    costEstimationEntries.forEach(costEstimationEntry => {

      const foundEntryIndex = currentEntries.findIndex(
        currentEntry => findEntryPredicate(currentEntry, costEstimationEntry));

      if (foundEntryIndex != -1) {

        const costEstimationQuantity = costEstimationEntry.quantity!;
        const entryQuantity = currentEntries[foundEntryIndex].quantity;

        let quantity = costEstimationQuantity - entryQuantity;

        if (currentEntries[foundEntryIndex].quantityFromDb) {

          const quantityFromDb = currentEntries[foundEntryIndex].quantityFromDb!;

          quantity = costEstimationQuantity - (quantityFromDb - entryQuantity);
        }

        costEstimationEntry.quantity = quantity <= 0 ? 0 : quantity;
      }
    });

    return costEstimationEntries;
  };

  updateEmergencyData = (
    updatedTarmedEntries: Array<TarmedDataDto>,
    emergencyProcedure?: BaseEntryDto,
    bastardProcedure?: BaseEntryDto) => {

    const { currentEntries } = this.state;

    let modifiedEntries = currentEntries;

    if (currentEntries.length > 0) {
      modifiedEntries = Array.from(currentEntries);
      modifiedEntries.forEach((entry, index) => {
        const foundEntry = updatedTarmedEntries
          .find(tarmedEntry => tarmedEntry.code == entry.code);

        if (foundEntry) {
          modifiedEntries[index] = {
            ...foundEntry
          };
        }
      });

      modifiedEntries = this.getUpdatedEntriesWithEmergencyProcedure(modifiedEntries, emergencyProcedure);

      this.reCalculatePrices(modifiedEntries);
    }

    this.setState({
      currentEntries: modifiedEntries,
      emergencyProcedure: emergencyProcedure,
      bastardProcedure: bastardProcedure,
      loadingEmergencyData: false
    });
  };

  getUpdatedEntriesWithEmergencyProcedure = (
    entries: Array<ExtendedBaseEntryDto>,
    emergencyProcedure?: BaseEntryDto): Array<ExtendedBaseEntryDto> => {

    const foundPreviousEmergencyProcedureIndex = entries
      .findIndex(entry => entry.emergencyProcedure
        || (!!emergencyProcedure
          && entry.code == emergencyProcedure.code));

    const modifiedEntries = Array.from(entries);
    if (foundPreviousEmergencyProcedureIndex != -1) {
      modifiedEntries.splice(foundPreviousEmergencyProcedureIndex, 1);
    }

    if (emergencyProcedure) {
      modifiedEntries.push(emergencyProcedure);
    }

    return modifiedEntries;
  };

  getMedicamentsChanges = (): MedicamentChanges => {

    const { initialEntries, currentEntries } = this.state;

    const medPredicate = (item: ExtendedBaseEntryDto) => !!item.medicamentId;

    const initialMedicaments = initialEntries.filter(medPredicate);
    const initialMedMap = _.keyBy(initialMedicaments, "medicamentId");

    const currentMedicaments = currentEntries.filter(medPredicate);
    const currentMedMap = _.keyBy(currentMedicaments, "medicamentId");

    const medChanges: MedicamentChanges = {};
    initialMedicaments.forEach(item => {
      const currentMed = currentMedMap[item.medicamentId];
      if (!currentMed) {
        medChanges[item.medicamentId] = item.quantity;
      }
    });

    currentMedicaments.forEach(item => {
      const initialMed = initialMedMap[item.medicamentId];
      if (!initialMed) {
        medChanges[item.medicamentId] = -item.quantity;
      } else {
        medChanges[item.medicamentId] = initialMed.quantity - item.quantity;
      }
    });

    return medChanges;
  };

  render() {
    return (
      <EntriesContext.Provider value={this.state}>
        {this.props.children}
      </EntriesContext.Provider>
    );
  }
}

export const EntriesConsumer = EntriesContext.Consumer;

export interface EntriesConsumerRenderProps extends State {
}

export const withEntriesContext = (Component: typeof React.Component | React.FunctionComponent<any>) => {
  return (props: any) => (
    <EntriesConsumer>
      {({ ...entriesProps }: EntriesConsumerRenderProps) => {
        return <Component {...props} {...entriesProps} />;
      }}
    </EntriesConsumer>
  );
};
